import type { CSSProperties } from "react";
import { belegAkzent, invoiceTexts, type InvoicePdfModel } from "@talentkontor/domain";
import { QrZahlteil } from "./QrZahlteil";
import "./invoice-paper.css";

/**
 * Beleg-Ansicht einer Rechnung (.paper aus dem Design-Paket). Sie liest dasselbe Modell wie das PDF (InvoicePdfModel aus
 * invoicePdfModelFromSource) und dieselben Texte (invoiceTexts): was hier steht, steht so auch im PDF und in der Druckansicht,
 * samt Registerangaben, Vertretung, Zahlungsziel, Bank- bzw. Auszahlungsblock, Logo und Farbe der Agentur.
 */

export function InvoicePaper({ m, logoUrl }: { m: InvoicePdfModel; logoUrl?: string | null }) {
  const tx = invoiceTexts(m);
  const accent = belegAkzent(m.accent); // gleiche Helligkeitsgrenze wie das PDF
  const recipientLines = m.recipient.addressLines.filter(Boolean);
  return (
    <article className={`paper tk-invoice-paper${accent ? " tk-akzent" : ""}`} lang={tx.lang} aria-label={`${tx.titleWord} ${m.number}`}
      style={accent ? ({ "--tk-beleg-akzent": accent } as CSSProperties) : undefined}>
      {logoUrl && <div className="tk-paper-logo"><img src={logoUrl} alt="" /></div>}
      <div className="row between">
        <strong>{m.issuer.name.toUpperCase()}</strong>
        <span className="tk-paper-head">{tx.head}</span>
      </div>
      <p>{m.issuer.addressLines.filter(Boolean).join(" · ")}</p>
      {(m.issuer.fields ?? []).map((f) => <small key={f.label} className="tk-paper-field">{f.label}: {f.value}</small>)}
      <h2>{tx.titleWord} {m.number}</h2>
      <p>
        {m.recipient.name}
        {recipientLines.length > 0 && <><br />{recipientLines.join(" · ")}</>}
        {(m.recipient.fields ?? []).map((f) => <span key={f.label}><br />{f.label}: {f.value}</span>)}
      </p>
      <div className="tk-paper-meta">
        {tx.meta.filter(([label]) => label !== tx.meta[0]![0]).map(([label, value]) => <p key={label}>{label}<br /><strong>{value}</strong></p>)}
      </div>
      <div className="tablewrap">
        <table>
          <thead><tr><th>{tx.t.item}</th><th>{tx.t.amount}</th></tr></thead>
          <tbody>
            {m.lines.map((l, i) => (
              <tr key={i}>
                <td>{l.quantity && l.quantity !== 1 ? `${tx.qty(l.quantity)} × ${tx.m(l.unitPriceCents)} · ` : ""}{l.description}</td>
                <td><span className="num">{tx.m(l.totalCents)}</span></td>
              </tr>
            ))}
            <tr><td>{tx.t.net}</td><td><span className="num">{tx.m(m.totals.netCents)}</span></td></tr>
            {tx.taxRows.map(([label, cents]) => <tr key={label}><td>{label}</td><td><span className="num">{tx.m(cents)}</span></td></tr>)}
            <tr><td><strong>{tx.t.gross}</strong></td><td><strong className="num">{tx.m(m.totals.grossCents)}</strong></td></tr>
          </tbody>
        </table>
      </div>
      {tx.taxEurText && <p className="muted">{tx.taxEurText}</p>}
      {tx.notice && <p>{tx.notice}</p>}
      <p>{tx.payText}</p>
      {tx.bankRows.length > 0 && (
        <p className="tk-paper-bank" data-sensibel="">
          <strong>{tx.bankTitle}</strong>
          {tx.bankRows.map(([label, value]) => <span key={label}><br />{label}: {value}</span>)}
        </p>
      )}
      {!tx.qrBill && m.qrBillNote && !m.creditNote && <p className="muted">{m.qrBillNote}</p>}
      {(m.footerLines ?? []).length > 0 && (
        <footer className="tk-paper-footer">{(m.footerLines ?? []).map((f) => <small key={f}>{f}</small>)}</footer>
      )}
      {tx.qrBill && <QrZahlteil bill={tx.qrBill} />}
    </article>
  );
}
